import { GridSortModel } from '@mui/x-data-grid';
import { AxiosResponse } from 'axios';
import { useEffect, useRef, useState, useTransition } from 'react';
import { usePaginationPreference } from './usePaginationPreference';

/**
 * A debounce hook that initializes from a ref instead of useState,
 * so it never fires a spurious state update on mount when the value
 * hasn't actually changed (unlike @uidotdev/usehooks useDebounce).
 */
function useStableDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(() => value);
  const isFirstMount = useRef(true);

  useEffect(() => {
    if (isFirstMount.current) {
      // Skip the first run — initial value is already correct via lazy useState.
      isFirstMount.current = false;
      return;
    }
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

export type Pagination = {
  pageNum?: number;
  pageSize?: number;
};

export type Sort = {
  sort?: string;
  order?: 'asc' | 'desc';
};

export type SearchResponse<T> = {
  queryResult: Array<T>;
  totalRecords: number;
};

export const DEFAULT_PAGINATION: Pagination = {
  pageNum: 0,
  pageSize: 10,
};

type Params = {
  apiPromise: (
    searchTerm: string,
    pageNum: number,
    pageSize: number,
    sort: string,
    order: string,
    f?: Array<string>
  ) => Promise<AxiosResponse>;
  searchTerm?: string;
  defaultSort?: Sort;
  defaultPagination?: Pagination;
  onError?: (error: any) => void;
  initialFilter?: Array<string>;
  pageKey?: string;
};

export const useSearch = <T>({
  apiPromise,
  searchTerm,
  defaultSort,
  defaultPagination,
  onError,
  initialFilter,
  pageKey,
}: Params) => {
  const [data, setData] = useState<SearchResponse<T> | null>(null);
  const [loading, setLoading] = useState(false);
  const [isPending, startTransition] = useTransition();

  const [filter, setFilter] = useState<Array<string>>(initialFilter || []);

  const [sortInfo, setSortInfo] = useState<Sort>(defaultSort);

  const defaultPageSize = defaultPagination?.pageSize ?? DEFAULT_PAGINATION.pageSize;
  const [persistedPaginationModel, setPersistedPaginationModel] = usePaginationPreference(
    pageKey ?? '',
    defaultPageSize
  );

  const [paginationInfo, setPaginationInfo] = useState<Pagination>({
    pageNum: defaultPagination?.pageNum ?? 1,
    pageSize: persistedPaginationModel.pageSize,
  });

  // Don't fetch until the persisted pagination preference has been read.
  // persistedPaginationModel is initialized synchronously from localStorage
  // via lazy useState, so pageSize is correct from the very first render.
  const debouncedSearchTerm = useStableDebouncedValue(searchTerm, 600);

  const onPaginationModelChange = (model: { pageSize: number; page: number }) => {
    if (model.pageSize !== paginationInfo.pageSize) {
      if (pageKey) {
        setPersistedPaginationModel({ page: 0, pageSize: model.pageSize });
      }
      setPaginationInfo((prevPagination) => ({
        ...prevPagination,
        pageSize: model.pageSize,
        pageNum: 1,
      }));
    } else {
      setPaginationInfo((prevPagination) => ({
        ...prevPagination,
        pageNum: model.page + 1,
      }));
    }
  };

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      setSortInfo({
        sort: field,
        order: sort as 'asc' | 'desc',
      });

      setPaginationInfo((prevPagination) => ({
        ...prevPagination,
        pageNum: 1,
      }));
    } else {
      setSortInfo(defaultSort);
    }
  };

  const processResponse = (response: AxiosResponse) => {
    const { data } = response.data;
    setData(data);
  };

  const prevSearchTermRef = useRef(debouncedSearchTerm);
  const prevFilterRef = useRef(filter);
  const prevPageNumRef = useRef(paginationInfo.pageNum);
  const prevPageSizeRef = useRef(paginationInfo.pageSize);
  const prevSortRef = useRef(sortInfo.sort);
  const prevOrderRef = useRef(sortInfo.order);
  const isFirstRun = useRef(true);

  useEffect(() => {
    // Capture previous values BEFORE updating refs
    const prevSearchTerm = prevSearchTermRef.current;
    const prevFilter = prevFilterRef.current;
    const prevPageNum = prevPageNumRef.current;
    const prevPageSize = prevPageSizeRef.current;
    const prevSort = prevSortRef.current;
    const prevOrder = prevOrderRef.current;

    const nothingChanged =
      !isFirstRun.current &&
      debouncedSearchTerm === prevSearchTerm &&
      filter === prevFilter &&
      paginationInfo.pageNum === prevPageNum &&
      paginationInfo.pageSize === prevPageSize &&
      sortInfo.sort === prevSort &&
      sortInfo.order === prevOrder;

    // Update refs for next run
    isFirstRun.current = false;
    prevSearchTermRef.current = debouncedSearchTerm;
    prevFilterRef.current = filter;
    prevPageNumRef.current = paginationInfo.pageNum;
    prevPageSizeRef.current = paginationInfo.pageSize;
    prevSortRef.current = sortInfo.sort;
    prevOrderRef.current = sortInfo.order;

    // Skip spurious re-renders where nothing actually changed
    if (nothingChanged) return;

    const searchOrFilterChanged =
      debouncedSearchTerm !== prevSearchTerm ||
      filter !== prevFilter;

    const fetchData = async () => {
      setLoading(true);

      const pagination = {
        pageNum: paginationInfo.pageNum,
        pageSize: paginationInfo.pageSize,
      };

      if (searchOrFilterChanged) {
        pagination.pageNum = 1;
        setPaginationInfo((prev) => ({ ...prev, pageNum: 1 }));
      }

      try {
        const response = await apiPromise(
          debouncedSearchTerm,
          pagination.pageNum,
          pagination.pageSize,
          sortInfo.sort,
          sortInfo.order
        );
        startTransition(() => {
          processResponse(response);
        });
      } catch (err) {
        if (onError) {
          onError(err);
        } else {
          console.error('Error fetching data:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    debouncedSearchTerm,
    paginationInfo.pageNum,
    paginationInfo.pageSize,
    sortInfo.sort,
    sortInfo.order,
    filter,
  ]);

  const reFetchData = async () => {
    setLoading(true);

    try {
      const response = await apiPromise(
        debouncedSearchTerm,
        paginationInfo.pageNum,
        paginationInfo.pageSize,
        sortInfo.sort,
        sortInfo.order
      );
      startTransition(() => {
        processResponse(response);
      });
    } catch (err) {
      if (onError) {
        onError(err);
      } else {
        console.error('Error fetching data:', err);
      }
    } finally {
      setLoading(false);
    }
  };

 return {
   data,
   setData,
   loading,
   isPending,
   sortInfo,
   paginationInfo,
   paginationModel: { page: (paginationInfo.pageNum ?? 1) - 1, pageSize: paginationInfo.pageSize ?? DEFAULT_PAGINATION.pageSize },
   filter,
   setFilter,
   onPaginationModelChange,
   handleSortModelChange,
   reFetchData
   };
};
